"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformSubFields = void 0;
const set_1 = __importDefault(require("lodash/set"));
const constants_1 = require("../constants");
const utils_1 = require("../utils");
const error_utils_1 = require("./error-utils");
/**
 * Transforms a list of field configs into field classes.
 *
 * Each class provides a set of utility functions allowing
 * React components to interact with each field type the
 * same way.
 *
 * @param {Array} fields List of field configs
 * @param {Object} entityData
 * @param {Boolean} globalIsReadOnly
 * @returns {Array} List of field classes
 */
function createFields(fields, entityData = {}, globalIsReadOnly = false) {
    return fields.map((field) => {
        // If a field is NOT an array type and has sub-fields we handle
        // it differently therefore declare it a 'subFields' type
        const type = field.subFields && field.type !== constants_1.FIELD_TYPE_NAMES.ARRAY
            ? 'subFields'
            : field.type;
        switch (type) {
            case 'subFields':
                return new SubField(field, entityData, globalIsReadOnly);
            case constants_1.FIELD_TYPE_NAMES.ARRAY:
                return new ArrayField(field, entityData, globalIsReadOnly);
            default:
                return new Field(field, entityData, globalIsReadOnly);
        }
    });
}
exports.default = createFields;
class Field {
    constructor(field, entityData, globalIsReadOnly) {
        this.getInitialValue = () => ({ [this.path]: this.value });
        this.setDefaultValue = ({ updatedFields, entityData }) => {
            const isValueSet = Object.prototype.hasOwnProperty.call(updatedFields, this.path) ||
                Object.prototype.hasOwnProperty.call(entityData, this.path);
            if (isValueSet || !this.value)
                return updatedFields;
            return Object.assign(Object.assign({}, updatedFields), { [this.path]: { value: this.value } });
        };
        this.toggleEditMode = () => {
            const shouldTurnOffReadyOnly = this.isReadOnly;
            if (shouldTurnOffReadyOnly) {
                // Only turn off "read only" mode if the initial
                // isReadOnly config was false
                this.isReadOnly = this.initialIsReadOnly;
            }
            else {
                this.isReadOnly = true;
            }
            return this;
        };
        this.getErrors = (0, error_utils_1.pipe)(error_utils_1.getEmptyRequiredFieldErrors, error_utils_1.mergeRequiredFieldErrorsIntoUpdatedFields);
        this.getData = ({ entityData }) => (0, utils_1.getInitialFieldData)(Object.assign({}, this), entityData, this.allowObjectTypes());
        this.isClear = (updatedFields) => {
            const isDirty = Object.prototype.hasOwnProperty.call(updatedFields, this.path);
            return isDirty ? (0, utils_1.isFieldEmpty)(updatedFields[this.path].value) : true;
        };
        this.clearErrors = (updatedFields) => {
            const updatedValuesCopy = Object.assign({}, updatedFields[this.path]);
            updatedValuesCopy.errors = [];
            return Object.assign(Object.assign({}, updatedFields), { [this.path]: updatedValuesCopy });
        };
        this.isConditional = () => {
            return Boolean(this.conditional);
        };
        this.initConditionalVisibility = (entityData) => { var _a, _b; return entityData[(_a = this.conditional) === null || _a === void 0 ? void 0 : _a.path] === ((_b = this.conditional) === null || _b === void 0 ? void 0 : _b.value); };
        Object.assign(this, field);
        this.value = this.getData({
            entityData
        }).value;
        this.initialIsReadOnly = this.isReadOnly;
        this.isReadOnly = globalIsReadOnly || Boolean(this.isReadOnly);
        this.isVisible = Object.prototype.hasOwnProperty.call(this, 'isVisible')
            ? this.isVisible
            : this.isConditional()
                ? this.initConditionalVisibility(entityData)
                : true;
        this.initialIsRequired = this.isRequired;
    }
    getPath() {
        return this.path;
    }
    getConfig() {
        return { [this.path]: this };
    }
    allowObjectTypes() {
        switch (this.type) {
            case constants_1.FIELD_TYPE_NAMES.MULTI_SELECT:
                return true;
            default:
                return false;
        }
    }
}
/**
 * A field that uses the 'subFields' prop to provide configs
 * for multiple fields within a single row.
 */
class SubField extends Field {
    constructor(field, entityData, globalIsReadOnly) {
        super(field, entityData, globalIsReadOnly);
        this.getInitialValue = () => Object.values(this.subFields).reduce((initialValues, field) => {
            return Object.assign(Object.assign({}, initialValues), field.getInitialValue());
        }, {});
        this.setDefaultValue = ({ updatedFields, entityData }) => Object.values(this.subFields).reduce((updatedFields, field) => {
            return field.setDefaultValue({ updatedFields, entityData });
        }, Object.assign({}, updatedFields));
        this.getErrors = ({ updatedFields, entityData }) => {
            return Object.values(this.subFields).reduce((updatedFields, field) => {
                return field.getErrors({
                    fields: [field],
                    entityData,
                    updatedFields
                });
            }, updatedFields);
        };
        this.isClear = (updatedFields) => {
            return Object.values(this.subFields).every((field) => {
                return field.isClear(updatedFields);
            });
        };
        this.clearErrors = (updatedFields) => {
            return Object.values(this.subFields).reduce((updatedFields, field) => field.clearErrors(updatedFields), Object.assign({}, updatedFields));
        };
        this.toggleEditMode = () => {
            const subFields = Object.assign({}, this.subFields);
            for (const key in subFields) {
                subFields[key] = subFields[key].toggleEditMode();
            }
            this.subFields = subFields;
            this.isReadOnly = !this.isReadOnly;
            return this;
        };
        this.path = this.path || (0, utils_1.getDerivedPath)(this);
        this.subFields = this.createSubFields(entityData, globalIsReadOnly);
        this.isReadOnly = globalIsReadOnly || Boolean(this.isReadOnly);
    }
    getPath() {
        return Object.values(this.subFields).reduce((paths, field) => paths.concat(field.getPath()), []);
    }
    getConfig() {
        const subFieldConfigs = Object.values(this.subFields).reduce((config, field) => (Object.assign(Object.assign({}, config), field.getConfig())), {});
        return Object.assign(Object.assign({}, subFieldConfigs), { [this.path]: this });
    }
    createSubFields(entityData, isReadOnly) {
        const subFields = Object.assign({}, this.subFields);
        for (const key in subFields) {
            subFields[key] = createFields([subFields[key]], entityData, isReadOnly)[0];
        }
        return subFields;
    }
}
/**
 * Like SubFields, multiple field configs are provided via
 * the 'subFields' prop. However in ArrayField these sub-fields
 * are rendered once for each value in an array (a.k.a field group).
 */
class ArrayField extends Field {
    constructor(field, entityData, globalIsReadOnly) {
        super(field, entityData, globalIsReadOnly);
        // getPath is not currently functional for this type.
        // It will need to be set up if array fields ever need to be nested.
        // getPath() {
        //   return this.subFields.map((field) => field.getPath());
        // }
        this.setInitialValue = () => {
            // Value of {} will result in display of a single empty field group
            const initialEmptyValue = !this.isRequired && this.isDefaultEmpty ? [] : [{}];
            this.value =
                this.getData({
                    entityData: this.entityData
                }).value || initialEmptyValue;
        };
        this.getInitialValue = () => this.subFields.reduce((initialValues, fieldGroup) => fieldGroup.reduce((fieldGroupValues, field) => (Object.assign(Object.assign({}, fieldGroupValues), field.getInitialValue())), initialValues), {});
        this.setDefaultValue = ({ updatedFields, entityData }) => {
            return this.subFields.reduce((updatedFields, fieldGroup) => fieldGroup.reduce((updatedFields, field) => field.setDefaultValue({ updatedFields, entityData }), updatedFields), Object.assign({}, updatedFields));
        };
        this.addItem = () => {
            this.value = [...this.value, {}];
            this.subFields = createArraySubFields(this.template, this.value, this.path);
            return { subFields: [...this.subFields] };
        };
        this.removeItem = ({ valueIndex, subFieldsIndex }) => {
            const newValue = [...this.value];
            // Value is set to null (vs deleting the value) so that we have a record of which
            // values belong to which index. This is needed when getting/setting data by path
            // (see createArraySubFields() and setEntityData())
            newValue[valueIndex] = null;
            this.value = newValue;
            const removedPaths = this.subFields[subFieldsIndex].reduce((paths, field) => paths.concat(field.getPath()), []);
            this.subFields = createArraySubFields(this.template, this.value, this.path);
            return {
                removedPaths,
                subFields: [...this.subFields],
                isEmpty: !this.value.filter(Boolean).length
            };
        };
        this.getData = ({ entityData }) => {
            return (0, utils_1.getInitialFieldData)(this.field, entityData, true);
        };
        this.isClear = (updatedFields) => {
            return this.subFields.every((fields) => fields.every((field) => field.isClear(updatedFields)));
        };
        this.clearErrors = (updatedFields) => {
            return this.subFields.reduce((updatedFields, fields) => fields.reduce((updatedFields, field) => field.clearErrors(updatedFields), Object.assign({}, updatedFields)), Object.assign({}, updatedFields));
        };
        this.getErrors = ({ updatedFields, entityData }) => {
            // Allow field group to be empty even if sub-fields are required
            if (this.isRequired === false) {
                if (this.isClear(updatedFields)) {
                    return this.clearErrors(updatedFields);
                }
            }
            return this.subFields.reduce((updatedFields, fieldGroup) => {
                return fieldGroup.reduce((updatedFields, field) => {
                    return field.getErrors({
                        fields: [field],
                        entityData,
                        updatedFields
                    });
                }, updatedFields);
            }, updatedFields);
        };
        this.toggleEditMode = () => {
            this.setInitialValue();
            this.isReadOnly = !this.isReadOnly;
            this.subFields = createArraySubFields(this.template, this.value, this.path, this.isReadOnly);
            return this;
        };
        this.field = field;
        this.entityData = entityData;
        this.setInitialValue();
        // Used to create new field groups
        this.template = transformSubFields(Object.assign({}, this.subFields));
        // subFields becomes a list of field groups to be rendered by a component
        this.subFields = createArraySubFields(this.template, this.value, this.path, globalIsReadOnly);
        this.isReadOnly = globalIsReadOnly || Boolean(this.isReadOnly);
    }
    getConfig() {
        const subFieldConfigs = this.subFields.reduce((config, fieldGroup) => {
            return fieldGroup.reduce((config, field) => {
                return Object.assign(Object.assign({}, config), field.getConfig());
            }, config);
        }, {});
        return Object.assign(Object.assign({}, subFieldConfigs), { [this.path]: this });
    }
    setEntityData({ entityData, updatedFields }) {
        const entityDataCopy = Object.assign({}, entityData);
        const value = this.value;
        const startPathRegExp = new RegExp(`^${this.path}`);
        let updatedPaths = Object.keys(updatedFields).filter((key) => key.match(startPathRegExp));
        const noUpdatesMade = !updatedPaths.length;
        const noInitialValue = !entityData[this.path];
        const hasDeletedValues = this.value.some((value) => value === null);
        const noChangeToEntityData = noUpdatesMade && noInitialValue && !hasDeletedValues;
        if (noChangeToEntityData)
            return entityDataCopy;
        updatedPaths.forEach((path) => {
            (0, set_1.default)(entityDataCopy, path, updatedFields[path].value);
        });
        const updatedEntityData = (0, utils_1.getPropertyValue)(entityDataCopy, this.path, true);
        // filter out any values that were deleted by user
        // which are represented as 'null' in this.value
        const filteredValues = value
            .map((value, index) => {
            if (value === null) {
                return value;
            }
            return updatedEntityData[index];
        })
            .filter(Boolean);
        return (0, set_1.default)(entityDataCopy, this.path, filteredValues);
    }
}
/**
 * Passes all subFields to transformGroupedFields() so
 * that the "template" used to create field groups has
 * its fields grouped in an <InlineRow /> component
 * when applicable.
 *
 * For example:
 *
 * {
 *  subFields:{
 *    one: {
 *      group: 'some-group',
 *      ...configs
 *    },
 *    two: {
 *      group: 'some-group',
 *      ...configs
 *    }
 *  }
 * }
 *
 * becomes
 *
 * {
 *  subFields:{
 *    onetwo: {
 *      group: 'some-group',
 *      type: 'inline',
 *      ...configs
 *    }
 *  }
 * }
 *
 * @param {Object} subFields
 * @returns {Object}
 */
function transformSubFields(subFields) {
    const transformedFields = (0, utils_1.transformGroupedFields)(Object.values(subFields));
    // turn subField array back into an object
    return transformedFields.reduce((fieldObj, field) => {
        const path = field.path || (0, utils_1.getDerivedPath)(field);
        return Object.assign(Object.assign({}, fieldObj), { [path]: field });
    }, {});
}
exports.transformSubFields = transformSubFields;
/**
 * Creates a list of field groups (one field group for each array value)
 * which will be rendered by a component. Each field is given a path
 * corresponding to its location in the array. It is also transformed
 * into a field class.
 *
 * Example output:
 *
 *    [{ ...phoneFieldClass, path: phone.0.number,
 *       ...nameFieldClass, path: phone.0.name }]
 *
 * @param {Object} template The props for each field group.
 * @param {Array} values Entire array of values.
 * @param {Number} parentPath Path to the array within entityData.
 * @param {Boolean} isReadOnly
 * @returns {Array}
 */
function createArraySubFields(template, values, parentPath, isReadOnly) {
    return values.reduce((fieldsArray, value, index) => {
        if (!value) {
            return fieldsArray;
        }
        const subFieldsWithArrayPaths = Object.values(updateFieldGroupPath({
            subFields: template,
            parentIndex: index,
            parentPath
        }));
        const valueAtPath = { [parentPath]: [] };
        valueAtPath[parentPath][index] = value;
        const fields = (0, utils_1.orderFields)(createFields(subFieldsWithArrayPaths, valueAtPath, isReadOnly));
        return [...fieldsArray, fields];
    }, []);
}
/**
 * Updates the path(s) for each field group in an array so that it
 * maps back to the location of the data within entityData.
 *
 * Example output:
 *
 *  {
 *    phone: {
 *      ...phoneProps,
 *      path: phone[0],
 *      index: 0,
 *      subFields: {
 *        number: {
 *          ...numberProps,
 *          path: phone[0].number
 *        }
 *      }
 *    },
 *  };
 *
 * @param {Object} subFields Fields to appear in a field group
 * @param {Number} parentIndex Index of field group within full array
 * @param {String} parentPath Path to array in entityData
 * @returns {Object}
 */
function updateFieldGroupPath({ subFields, parentIndex, parentPath }) {
    const subFieldsCopy = Object.assign({}, subFields);
    for (const key in subFieldsCopy) {
        const field = subFieldsCopy[key];
        const subPath = field.path
            ? `[${parentIndex}].${field.path}`
            : `[${parentIndex}].${(0, utils_1.getDerivedPath)(field)}`;
        let subFields = field.subFields;
        if (subFields) {
            subFields = updateFieldGroupPath({ subFields, parentIndex, parentPath });
        }
        subFieldsCopy[key] = Object.assign(Object.assign({}, field), { path: `${parentPath}${subPath}`, subFields, index: parentIndex });
    }
    return subFieldsCopy;
}
//# sourceMappingURL=field-factory.js.map